import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { DataRow } from 'src/components/shared/DataRow'
import { SectionCard } from 'src/components/shared/SectionCard'
import { AdminUpdatePartnerDriver } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { PartnerDriverDetails } from 'src/interfaces/partnerDriver'
import { getPartnerDriverSaving } from 'src/selectors/partnerDriver'
import { PartnerDriverProfileForm } from './PartnerDriverProfileForm'
import { toProfileFormValues, toUpdatePartnerDriverBody } from './PartnerDriverProfileForm.mapper'
import type { PartnerDriverProfileFormValues } from './PartnerDriverProfileForm.interface'

interface Props {
  driver: PartnerDriverDetails
  /** From the summary row; `null` while it loads or when the driver has none. */
  otpCode: string | null
  onSaved(): void
}

const PartnerDriverProfileCardComponent = ({ driver, otpCode, onSaved }: Props) => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const isSaving = useSelector(getPartnerDriverSaving)
  // PUT /contractors/:id. Without it the profile still shows, read-only (ENG-1207 part 2).
  const canManageContractor = useHasPermission(InternalPermission.ContractorsManage)

  const initialValues = useMemo(() => {
    return toProfileFormValues(driver)
  }, [driver])

  const handleSubmit = useCallback(
    async (values: PartnerDriverProfileFormValues) => {
      const body = toUpdatePartnerDriverBody(initialValues, values)

      if (Object.keys(body).length === 0) {
        return
      }

      // Backend refusals ("Cannot change email while yardstik report is pending.", "Phone number
      // is already in use", duplicate email) reach the error toast verbatim.
      const result = await asyncDispatchWithErrorToast<boolean>(
        AdminUpdatePartnerDriver.request({ partnerDriverId: driver.id, body }),
      )

      if (!result) {
        return
      }

      snackbar?.enqueueSnackbar('Profile updated', { variant: 'success' })
      onSaved()
    },
    [initialValues, asyncDispatchWithErrorToast, driver.id, snackbar, onSaved],
  )

  return (
    <SectionCard title="Profile" dataTestId="partner-driver-profile-card">
      <div>
        <DataRow title="Partner Driver ID" text={driver.id} textClassName={tw`font-mono`} />
        <DataRow title="External ID" text={driver.externalId ?? '—'} />
        <DataRow title="OTP code" text={otpCode ?? '—'} textClassName={tw`font-mono`} />
      </div>
      <PartnerDriverProfileForm
        initialValues={initialValues}
        isSaving={isSaving}
        isReadOnly={!canManageContractor}
        onSubmit={handleSubmit}
      />
    </SectionCard>
  )
}

export const PartnerDriverProfileCard = memo(PartnerDriverProfileCardComponent)
